/** Stage Walk-through — auto-advances through stage codes for guided learning.
 *
 * A floating control bar that lets the user step through the dynamic stage
 * catalogue (Prev / Pause / Next / speed) so a junior engineer learns the
 * sequence: B.1.1 → B.1.2 → … → I.4.1. Pauses on Run-stage trigger.
 */
import React, { useEffect, useState, useRef } from "react";
import { Play, Pause, ChevronLeft, ChevronRight, X, GraduationCap } from "lucide-react";

const SPEEDS = [
  { label: "5s", ms: 5000 },
  { label: "10s", ms: 10000 },
  { label: "20s", ms: 20000 },
];

export default function StageWalkthrough({ catalogue, activeCode, onPick, onClose }) {
  // Only step through L2 codes (B.1.1, etc.). Skip top-level A/B/C parents.
  const codes = (catalogue || [])
    .filter((c) => c.code && c.code.includes("."))
    .map((c) => ({ code: c.code, name: c.name }));
  const idx = Math.max(0, codes.findIndex((c) => c.code === activeCode));
  const [playing, setPlaying] = useState(false);
  const [speedIdx, setSpeedIdx] = useState(1);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!playing) {
      clearInterval(intervalRef.current);
      return undefined;
    }
    intervalRef.current = setInterval(() => {
      const next = codes[(idx + 1) % codes.length];
      if (next) onPick(next.code);
    }, SPEEDS[speedIdx].ms);
    return () => clearInterval(intervalRef.current);
  }, [playing, idx, speedIdx, codes, onPick]);

  if (codes.length === 0) return null;
  const current = codes[idx] || codes[0];

  return (
    <div data-testid="stage-walkthrough"
        style={{ position: "fixed", bottom: 16, left: "50%",
                      transform: "translateX(-50%)",
                      background: "var(--pf-surface)",
                      border: "2px solid var(--pf-accent)",
                      borderRadius: 8, padding: "8px 14px",
                      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
                      display: "flex", alignItems: "center", gap: 10,
                      zIndex: 9999, fontSize: 12,
                      fontFamily: "'JetBrains Mono', monospace" }}>
      <GraduationCap size={16} color="var(--pf-accent)" />
      <span style={{ color: "var(--pf-text)", fontWeight: 700 }}>
        Walk-through
      </span>
      <span style={{ color: "var(--pf-text-muted)" }}>
        {idx + 1} / {codes.length}
      </span>
      <span style={{ color: "var(--pf-spark)", maxWidth: 280,
                          overflow: "hidden", textOverflow: "ellipsis",
                          whiteSpace: "nowrap" }}>
        {current.code} · {current.name}
      </span>

      <button type="button"
          data-testid="walkthrough-prev"
          className="btn btn-ghost"
          style={{ padding: 4 }}
          disabled={idx === 0}
          onClick={() => onPick(codes[Math.max(0, idx - 1)].code)}>
        <ChevronLeft size={14} />
      </button>

      <button type="button"
          data-testid="walkthrough-toggle"
          className="btn btn-ghost"
          style={{ padding: 4,
                          background: playing ? "var(--pf-accent)" : "transparent",
                          color: playing ? "#fff" : "var(--pf-text)" }}
          onClick={() => setPlaying((p) => !p)}>
        {playing ? <Pause size={14} /> : <Play size={14} />}
      </button>

      <button type="button"
          data-testid="walkthrough-next"
          className="btn btn-ghost"
          style={{ padding: 4 }}
          disabled={idx === codes.length - 1}
          onClick={() => onPick(codes[Math.min(codes.length - 1, idx + 1)].code)}>
        <ChevronRight size={14} />
      </button>

      <button type="button"
          data-testid="walkthrough-speed"
          className="btn btn-ghost"
          style={{ padding: "2px 6px", fontSize: 11 }}
          onClick={() => setSpeedIdx((s) => (s + 1) % SPEEDS.length)}
          title="Cycle playback speed">
        {SPEEDS[speedIdx].label}
      </button>

      <button type="button"
          data-testid="walkthrough-close"
          className="btn btn-ghost"
          style={{ padding: 4 }}
          onClick={onClose}>
        <X size={14} />
      </button>
    </div>
  );
}
